import { AfterViewInit, Component, ElementRef, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { ConfigurationService } from '../../../services';

@Component({
  selector: 'rl-graph',
  templateUrl: './graph.component.html'
})
export class GraphComponent implements OnInit, AfterViewInit, OnChanges {

  skin = this.configurationService.getSkin();

  @Input()
  data: Array<number>;

  @ViewChild('canvas')
  canvasEl: ElementRef<HTMLCanvasElement>;

  @Input()
  options;

  private legend = !1;
  private context: any;
  private maxPositive: number;
  private maxNegative: number;
  private horizontalScale: any;
  private verticalScale: any;
  private middle: any;
  private max: any;


  private _options = {
    paddingBottom: 0,
    paddingLeft: 0,
    paddingRight: 0,
    paddingTop: 0,
    showCircle: false,
    circle: '#55AA77',
    circleSize: 4,
    background: 'transparent',
    showZeroLine: false,
    centerZero: false,
    zeroLineColor: '#EEE',
    lineColor: '#8E8E8E',
    lineWidth: 4,
    showBounds: false,
    bounds: '#8888EE',
    boundsHeight: 14,
    boundsFont: 'Arial',
    showLegend: false,
    legend: '#8888EE',
    legendHeight: 14,
    legendFont: 'Arial'
  };


  constructor(
    private readonly configurationService: ConfigurationService
  ) {
    if (this.skin === 'modern') {
      this._options.lineColor = '#ffbe77';
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.options) {
      for (const key in this._options) {
        if (this.options.hasOwnProperty(key)) {
          this._options[key] = this.options[key];
        }
      }
    }
    if (this.data && this.context) {
      this.context.clearRect(0, 0, this.canvasEl.nativeElement.width, this.canvasEl.nativeElement.height);
      this.draw();
    }
  }

  ngOnInit() {
    // if (this.options.parentSize) {
    //   this.canvas.height = this.canvas.parentElement.offsetHeight;
    //   this.canvas.width = this.canvas.parentElement.offsetWidth;
    // }

    if (this._options.showLegend) {
      this._options.paddingLeft = this._options.paddingLeft || this._options.legendHeight * 2;
      this._options.paddingRight = this._options.paddingRight || this._options.legendHeight * 2;
      this._options.paddingBottom = this._options.paddingBottom || this._options.legendHeight * 2.5;
    }

    if (this._options.showBounds) {
      this._options.paddingLeft = this._options.paddingLeft || this._options.boundsHeight / 1.4;
      this._options.paddingTop = this._options.paddingTop || this._options.boundsHeight * 1.4;
      this._options.paddingBottom = this._options.paddingBottom || this._options.boundsHeight * 1.4;
    }

    if (this._options.showCircle) {
      this._options.paddingRight = this._options.paddingRight || this._options.circleSize;
      this._options.paddingTop = this._options.paddingTop || this._options.circleSize;
      this._options.paddingBottom = this._options.paddingBottom || this._options.circleSize;
    }


    this._options.paddingTop = this._options.paddingTop || this._options.lineWidth;
    this._options.paddingBottom = this._options.paddingBottom || this._options.lineWidth;
  }

  ngAfterViewInit(): void {
    this.context = this.canvasEl.nativeElement.getContext('2d');
    this.draw();
  }

  /**
   * Draw the graph
   */
  draw() {
    this.drawBackground();
    this.computeScale();
    this.drawMiddle();
    this.drawScale();
    this.drawData();
    this.drawCircle();
    this.drawBounds();
    this.drawLegend();
  }

  /**
   * Draw canvas brackground
   */
  drawBackground() {
    this.context.fillStyle = this._options.background;
    this.context.fillRect(0, 0, this.canvasEl.nativeElement.width, this.canvasEl.nativeElement.height);
  }

  /**
   * Compute scale for given canvas size
   */
  computeScale() {
    let height = this.canvasEl.nativeElement.height;
    let width = this.canvasEl.nativeElement.width;

    this.maxPositive = Math.max.apply(null, this.data);
    this.maxNegative = Math.min.apply(null, this.data);

    if (this.maxPositive === this.maxNegative) {
      if (this.maxPositive === 0) { this.maxPositive = 1; }
      if (this.maxPositive > 0) { this.maxNegative = 0; }
      if (this.maxPositive < 0) { this.maxPositive = 0; }
    }

    this.maxPositive = Math.abs(this.maxPositive);
    this.maxNegative = Math.abs(this.maxNegative);
    this.max = Math.max(this.maxPositive, this.maxNegative);

    height -= this._options.paddingTop + this._options.paddingBottom;
    width -= this._options.paddingLeft + this._options.paddingRight;

    this.horizontalScale = width / (this.data.length - 1);

    if (this._options.centerZero) {
      this.middle = Math.round(height / 2);
      this.verticalScale = this.middle / this.max;
    } else {
      this.verticalScale = height / (this.maxPositive - this.maxNegative);
      this.middle = Math.round(this.maxPositive * this.verticalScale);
    }
  }

  /**
   * Draw middle line of a graph
   */
  drawMiddle() {
    if (!this._options.showZeroLine) { return; }

    this.context.moveTo(this._options.paddingLeft, this.middle +  this._options.paddingTop);
    this.context.lineTo(this.canvasEl.nativeElement.width - this._options.paddingRight, this.middle + this._options.paddingTop);
    this.context.strokeStyle = this._options.zeroLineColor;
    this.context.stroke();
  }

  /**
   * Draw scale line
   */
  drawScale() {
    if (!this._options.showBounds) { return; }

    this.context.moveTo(this._options.paddingLeft, this._options.paddingTop);
    this.context.lineTo(this._options.paddingLeft, this.canvasEl.nativeElement.height - this._options.paddingBottom);
    this.context.strokeStyle = this._options.zeroLineColor;
    this.context.stroke();
  }

  /**
   * Draw data line
   */
  drawData() {
    let i = this.data.length - 2;

    this.context.strokeStyle = this._options.lineColor;
    this.context.lineWidth = this._options.lineWidth;

    this.context.beginPath();
    this.context.moveTo.apply(this.context, this.getPointCoordinates(this.data.length - 1));

    for (; i >= 0; i--) {
      this.context.lineTo.apply(this.context, this.getPointCoordinates(i));
    }

    this.context.stroke();
  }
  /**
   * Compute coordinates for asked data index
   */
  getPointCoordinates(index) {
    return [
      this._options.paddingLeft + (index * this.horizontalScale),
      (this.middle + this._options.paddingTop) - (this.verticalScale * this.data[index])
    ];
  }

  /**
   * Draw circle to the end of the graph
   */
  drawCircle() {
    if (!this._options.showCircle) { return; }

    const lastPoint = this.getPointCoordinates(this.data.length - 1);

    this.context.fillStyle = this._options.circle;
    this.context.beginPath();
    this.context.arc(lastPoint[0], lastPoint[1], this._options.circleSize, 0, 2 * Math.PI);
    this.context.closePath();
    this.context.fill();
  }

  /**
   * Draw scale bounds text
   */
  drawBounds() {
    if (!this._options.showBounds) { return; }

    const topBound    = this._options.centerZero ? this.max : this.maxPositive;
    const bottomBound = this._options.centerZero ? this.max : this.maxNegative;

    this.context.font = this._options.boundsHeight + 'px ' + this._options.boundsFont;
    this.context.fillStyle = this._options.bounds;
    this.context.textBaseline = 'middle';
    this.context.textAlign = 'center';
    this.context.fillText(topBound, this._options.paddingLeft - (this._options.showLegend ? this._options.paddingLeft / 2 : 0) , this._options.paddingTop - ( this._options.showLegend ? 0 : this._options.boundsHeight));
    this.context.fillText((bottomBound ? '-' : '') + bottomBound, this._options.paddingLeft - (this._options.showLegend ? this._options.paddingLeft / 2 : 0), this.canvasEl.nativeElement.height - this._options.paddingBottom + (this._options.showLegend ? 0 : this._options.boundsHeight));
  }

  /**
   * Draw graph legend
   */
  drawLegend() {
    let i    = this.data.length;
    let  c;

    if (!this._options.showLegend || !this.legend) { return; }

    this.context.font = this._options.legendHeight + 'px ' + this._options.legendFont;
    this.context.fillStyle = this._options.legend;
    this.context.textBaseline = 'middle';
    this.context.textAlign = 'center';

    for (; i >= 0; i--) {
      c = this.getPointCoordinates(i);
      this.context.fillText(this.legend[i], c[0], this.canvasEl.nativeElement.height - this._options.paddingBottom / 2);
    }
  }
}
